import React, { useEffect } from 'react';
import { 
  ThemeProvider, 
  CssBaseline, 
  Box, 
  Typography, 
  Paper,
  Alert,
  Chip
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { atomDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import theme from './theme';
import SideNav from './SideNav';

export default function DocumentationPage() {
  const location = useLocation();

  // Scroll to section if hash is present
  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [location.hash]);

  const CodeBlock = ({ code, language = 'javascript' }) => (
    <Box sx={{ my: 2 }}>
      <SyntaxHighlighter
        language={language}
        style={atomDark}
        customStyle={{
          borderRadius: '8px',
          padding: '16px',
          fontSize: '14px'
        }}
      >
        {code}
      </SyntaxHighlighter>
    </Box>
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: 'flex' }}>
        {/* Navigation Sidebar */}
        <SideNav paperBg={'background.paper'} titleColor={'primary.main'} />

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 4,
            // width: `calc(100% - ${drawerWidth}px)`
          }}
        >
          <Typography variant="h1" sx={{ mb: 4, fontWeight: 700 }}>
            Documentation
          </Typography>

          {/* Getting Started Section */}
          <Box id="getting-started" sx={{ mb: 6 }}>
            <Typography variant="h2" sx={{ mb: 3, fontWeight: 600 }}>
              Getting Started
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
              Welcome to the Polyguard SDK documentation. This guide will help you integrate secure, 
              privacy-preserving identity verification into your applications.
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              <strong>Prerequisites:</strong> Basic knowledge of JavaScript/TypeScript and your chosen backend framework.
            </Alert>

            <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
              Quick Start
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Install the Polyguard SDK via npm:
            </Typography>
            <CodeBlock code="npm install @polyguard/sdk" language="bash" />
            
            <Typography variant="body1" sx={{ mb: 2 }}>
              Initialize the client in your application:
            </Typography>
            <CodeBlock code={`import { PolyguardClient } from '@polyguard/sdk';

const client = new PolyguardClient({
  appId: 'your-app-id',
  proxyUrl: '/api/polyguard',    // your server-side proxy (see Authentication)
  apiServer: 'api.polyguard.ai', // used for the WebSocket upgrade
  requiredProofs: ['Full Name'], // optional, default shown
  scanType: 'single'             // 'single' (default) or 'multi'
});`} />

            <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
              Scan Types
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Polyguard supports two verification scan types:
            </Typography>
            <Paper elevation={1} sx={{ p: 3, mb: 3, backgroundColor: 'background.default' }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>Single Scan</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Uses a single QR code for basic identity verification. Suitable for most standard verification flows.
                </Typography>
              </Box>
              <Box>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>Multi Scan (Advanced)</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Uses a sequence of multiple QR codes to verify the physical presence of the user. 
                  This enhanced security method ensures the user is physically present during verification, 
                  preventing spoofing attacks and providing stronger identity assurance.
                </Typography>
              </Box>
            </Paper>
          </Box>

          {/* Authentication Section */}
          <Box id="authentication" sx={{ mb: 6 }}>
            <Typography variant="h2" sx={{ mb: 3, fontWeight: 600 }}>
              Authentication
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
              Polyguard uses app-based authentication with configurable proof requirements.
              Each application has a unique App ID that determines available proof types and verification methods.
            </Typography>

            <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
              API Keys and the Proxy Pattern
            </Typography>
            <Paper elevation={1} sx={{ p: 3, mb: 3, backgroundColor: 'background.default', borderLeft: '4px solid #d32f2f' }}>
              <Typography variant="body1" sx={{ mb: 1, fontWeight: 700 }}>
                Never embed a Polyguard API key in browser code.
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Anything in your JavaScript bundle is public. The SDK throws if you pass <code>apiKey</code> in a browser context.
                Instead, run the SDK&apos;s lightweight proxy on your server and have the browser call your origin.
              </Typography>
            </Paper>
            <Typography variant="body1" sx={{ mb: 2 }}>
              <strong>Browser:</strong> point the SDK at your proxy route via <code>proxyUrl</code>:
            </Typography>
            <CodeBlock code={`import { PolyguardClient } from '@polyguard/sdk';

const client = new PolyguardClient({
  appId: 'your-app-id',
  proxyUrl: '/api/polyguard',
  apiServer: 'api.polyguard.ai',
});`} />
            <Typography variant="body1" sx={{ mt: 2, mb: 2 }}>
              <strong>Server (Vercel Edge):</strong> drop this file in at <code>api/polyguard/[...path].js</code> and set <code>POLYGUARD_API_KEY</code> in your Vercel project settings. The handler validates an allowlist of SDK paths, strips any inbound <code>x-pg-api-key</code> or <code>authorization</code> header, and injects the server-held key.
            </Typography>
            <CodeBlock code={`import { createProxyHandler } from '@polyguard/sdk/server';

export const config = { runtime: 'edge' };

export default createProxyHandler({
  apiKey: process.env.POLYGUARD_API_KEY,
  apiServer: 'api.polyguard.ai',
});`} />
            <Typography variant="body2" sx={{ mt: 2, mb: 3, color: 'text.secondary' }}>
              The WebSocket realtime connection still goes directly to <code>apiServer</code> &mdash; it&apos;s authenticated with the short-lived ticket the proxy returns, not with the API key.
            </Typography>

            <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
              App ID Configuration
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Your App ID is provided when you register your application with Polyguard. 
              It determines which proof types are available and how verification flows work.
            </Typography>
            
            <Paper elevation={1} sx={{ p: 3, mb: 3, backgroundColor: 'background.default' }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Available Proof Types:</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip label="Full Name" color="primary" />
                <Chip label="Geographic Region" color="primary" />
                <Chip label="Phone Number" color="primary" />
                <Chip label="Age" color="primary" />
                <Chip label="Employer" color="secondary" />
              </Box>
            </Paper>
          </Box>

          {/* API Reference Section */}
          <Box id="api-reference" sx={{ mb: 6 }}>
            <Typography variant="h2" sx={{ mb: 3, fontWeight: 600 }}>
              API Reference
            </Typography>

            <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
              PolyguardClient
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              The main client class for interacting with Polyguard services.
            </Typography>

            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              Constructor
            </Typography>
            <CodeBlock code={`new PolyguardClient(options)

// Options:
{
  appId: string,          // Your application ID (required)
  proxyUrl?: string,      // Your server-side proxy route (e.g. '/api/polyguard').
                          // When set, all HTTP calls go through it.
  apiServer: string,      // API server host (e.g. 'api.polyguard.ai'). Still used
                          // for the WebSocket upgrade even when proxyUrl is set.
  requiredProofs?: string[], // Defaults to ['Full Name']
  scanType?: 'single' | 'multi', // Defaults to 'single'
  redirectUrl?: string,
  callbackUrl?: string,
  cookieName?: string,
  // apiKey is server-only. Passing it in the browser throws unless you
  // also set iKnowThisIsInsecure: true (not recommended).
  apiKey?: string,
}`} />

            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              Methods
            </Typography>

            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
              verify()
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Opens a modal with a QR code (or deep link on mobile) and resolves with a JWT string when verification completes. Rejects if the user cancels.
            </Typography>
            <CodeBlock code={`try {
  const jwt = await client.verify();
  // Send JWT to your backend for verification, or decode client-side if appropriate
  console.log(jwt);
} catch (e) {
  console.error('Verification cancelled or failed', e);
}`} />

            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
              require(expectedProofs)
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Convenience wrapper around <code>verify()</code> that decodes the JWT payload client-side and checks that provided proof values match the expected values.
            </Typography>
            <CodeBlock code={`const ok = await client.require({
  'Full Name': 'Alice Example'
});
if (!ok) {
  // Handle mismatch or cancellation
}`} />
          </Box>

          {/* SDK Integration Section */}
          <Box id="sdk-integration" sx={{ mb: 6 }}>
            <Typography variant="h2" sx={{ mb: 3, fontWeight: 600 }}>
              SDK Integration
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
              The Polyguard SDK supports multiple integration patterns to fit different application architectures.
            </Typography>

            <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
              WebSocket Integration
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Real-time verification via a WebSocket-backed modal. On desktop, users scan a QR code; on mobile, a deep link button opens the Polyguard app.
            </Typography>
            <CodeBlock code={`const client = new PolyguardClient({
  appId: 'your-app-id',
  proxyUrl: '/api/polyguard',
  apiServer: 'api.polyguard.ai',
  requiredProofs: ['Full Name'],
  scanType: 'single'
});

try {
  const jwt = await client.verify();
  // Submit JWT to your backend for verification
  await fetch('/verify', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ jwt }) });
} catch (e) {
  // User cancelled or error occurred
}`} />
          </Box>

          {/* Webhooks Section */}
          <Box id="webhooks" sx={{ mb: 6 }}>
            <Typography variant="h2" sx={{ mb: 3, fontWeight: 600 }}>
              Webhooks
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
              Receive real-time notifications about verification events via webhooks.
            </Typography>

            <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
              Setup
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Configure your webhook endpoint to receive POST requests:
            </Typography>
            <CodeBlock code={`// Express.js example
app.post('/webhook/polyguard', express.json(), (req, res) => {
  const { event, data } = req.body;
  
  switch (event) {
    case 'verification.completed':
      // Handle successful verification
      console.log('User verified:', data.userId);
      break;
    case 'verification.failed':
      // Handle failed verification
      console.log('Verification failed:', data.reason);
      break;
  }
  
  res.status(200).send('OK');
});`} />

            <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
              Event Types
            </Typography>
            <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Webhook Events:</Typography>
              <Box component="ul" sx={{ pl: 2 }}>
                <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                  <strong>verification.started</strong> - Verification process initiated
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                  <strong>verification.completed</strong> - Verification successfully completed
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                  <strong>verification.failed</strong> - Verification failed or rejected
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                  <strong>verification.expired</strong> - Verification session expired
                </Typography>
              </Box>
            </Paper>
          </Box>

          {/* Examples Section */}
          <Box id="examples" sx={{ mb: 6 }}>
            <Typography variant="h2" sx={{ mb: 3, fontWeight: 600 }}>
              Examples
            </Typography>

            <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
              Basic Age Verification
            </Typography>
            <CodeBlock code={`import { PolyguardClient } from '@polyguard/sdk';

const client = new PolyguardClient({
  appId: 'your-app-id',
  proxyUrl: '/api/polyguard',
  apiServer: 'api.polyguard.ai',
  requiredProofs: ['Age'],
  scanType: 'single'
});

async function verifyAge() {
  try {
    const jwt = await client.verify();
    console.log('JWT:', jwt);
  } catch (error) {
    console.error('Verification cancelled or failed:', error);
  }
}`} />

            <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
              Enhanced Physical Presence Verification
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Use multi scan type for enhanced security with physical presence verification:
            </Typography>
            <CodeBlock code={`const client = new PolyguardClient({
  appId: 'your-app-id',
  proxyUrl: '/api/polyguard',
  apiServer: 'api.polyguard.ai',
  requiredProofs: ['Full Name', 'Geographic Region'],
  scanType: 'multi'
});

const jwt = await client.verify();
// Process the JWT as needed`} />

            <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
              React Component Example
            </Typography>
            <CodeBlock code={`import React, { useState } from 'react';
import { PolyguardClient } from '@polyguard/sdk';

function VerificationComponent() {
  const [isVerifying, setIsVerifying] = useState(false);
  const [jwt, setJwt] = useState(null);
  const [useMultiScan, setUseMultiScan] = useState(false);

  const client = new PolyguardClient({
    appId: process.env.REACT_APP_POLYGUARD_APP_ID,
    proxyUrl: '/api/polyguard',
    apiServer: 'api.polyguard.ai',
    requiredProofs: ['Full Name', 'Age'],
    scanType: useMultiScan ? 'multi' : 'single'
  });

  const handleVerification = async () => {
    setIsVerifying(true);
    try {
      const token = await client.verify();
      setJwt(token);
    } catch (error) {
      console.error('Verification cancelled or failed:', error);
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div>
      <label>
        <input
          type="checkbox"
          checked={useMultiScan}
          onChange={(e) => setUseMultiScan(e.target.checked)}
        />
        Use Multi Scan (Enhanced Physical Presence)
      </label>

      {!jwt ? (
        <button onClick={handleVerification} disabled={isVerifying}>
          {isVerifying ? 'Verifying...' : 'Start Verification'}
        </button>
      ) : (
        <div>
          <h3>Verification Complete!</h3>
          <p>JWT: {jwt.slice(0, 16)}…</p>
          <p>Scan Type: {useMultiScan ? 'Multi (Physical Presence)' : 'Single'}</p>
        </div>
      )}
    </div>
  );
}`} language="jsx" />
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
} 